import type { RevenueLedgerDailyRow, RevenueLedgerRow } from '@cortex/domain-ac-mens-hub-api';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  type TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';
import { useSeriesToggle } from '../../../hooks/useSeriesToggle.js';
import {
  AXIS_TICK_STYLE,
  CHART_COLORS_EXTENDED,
  GRID_PROPS,
  LEGEND_CLICKABLE_PROPS,
  TOOLTIP_CONTENT_STYLE,
} from '../../../lib/chart-theme.js';
import { GRANULARITY_OPTIONS, type Granularity } from '../../../lib/kpi-aggregation.js';
import { formatYen } from '../../../lib/revenue-aggregation.js';
import {
  buildLedgerChartRows,
  isLedgerIncreaseGranularity,
  LEDGER_CHART_SERIES,
  LEDGER_TOTAL_LABEL_FONT_SIZE,
  type LedgerChartRow,
  type LedgerChartSeriesKey,
  ledgerTotalLabelLayout,
} from '../../../lib/revenue-ledger-view.js';
import { ChartFilterBar } from '../../chart/ChartFilterBar.js';

/**
 * 系列の色。取消は赤、営業外収益は売上高と見分けやすい黄。
 * @graph-connects none
 */
const SERIES_COLORS: Record<LedgerChartSeriesKey, string> = {
  membershipMonthly: CHART_COLORS_EXTENDED[0],
  membershipBimonthly: CHART_COLORS_EXTENDED[1],
  penalty: CHART_COLORS_EXTENDED[2],
  buyout: CHART_COLORS_EXTENDED[5],
  cancel: CHART_COLORS_EXTENDED[3],
  nonOperating: CHART_COLORS_EXTENDED[6],
};

/**
 * 売上高の積み上げに入る系列（吹き出しで売上高の合計の上に並べる）。
 * @graph-connects LEDGER_CHART_SERIES [references] チャートの系列
 */
const SALES_SERIES = LEDGER_CHART_SERIES.filter((series) => series.stack === 'sales');

/**
 * グラフの左右・下の余白（px）。上の余白は棒の上の数字の置き方で決める。
 * @graph-connects none
 */
const CHART_MARGIN = { right: 16, bottom: 4, left: 8 } as const;

/**
 * 縦軸の目盛りの幅（px。recharts の既定と同じ値を明示し、棒を描く範囲の幅の計算に使う）。
 * @graph-connects none
 */
const Y_AXIS_WIDTH = 60;

/**
 * 縦軸の目盛りの数。取消をゼロの下に積むと負の側に目盛り1つぶんの範囲が要るので、既定の 5 より細かくして
 * 負の側の空きを小さくする。
 * @graph-connects none
 */
const Y_AXIS_TICK_COUNT = 7;

/**
 * 売上高の合計を棒の上に置くための、高さ 0 の棒の形。何も描かない（形を指定すると高さ 0 の棒も数字の位置として残る）。
 * @graph-connects none
 */
function EmptyShape() {
  return null;
}

/**
 * 高さ 0 の棒の値。売上高の積み上げの最後に置き、正の側の上端（取消を除く）に数字の位置を作る。
 * @graph-connects none
 */
function zeroValue(): number {
  return 0;
}

/**
 * 棒の上の売上高の合計の数字。横書きは棒の上の中央、縦書きは棒の上から上へ伸ばす。
 * @graph-connects formatYen [calls] 金額の書式
 * @graph-connects LEDGER_TOTAL_LABEL_FONT_SIZE [references] 数字の文字の大きさ
 */
function SalesTotalLabel(props: {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  value?: unknown;
  placement: 'horizontal' | 'vertical';
}) {
  const { x, y, width, value, placement } = props;
  if (
    typeof x !== 'number' ||
    typeof y !== 'number' ||
    typeof width !== 'number' ||
    typeof value !== 'number'
  ) {
    return null;
  }
  const cx = x + width / 2;
  const cy = y - 4;
  return (
    <text
      x={cx}
      y={cy}
      transform={placement === 'vertical' ? `rotate(-90 ${String(cx)} ${String(cy)})` : undefined}
      textAnchor={placement === 'vertical' ? 'start' : 'middle'}
      dominantBaseline={placement === 'vertical' ? 'central' : undefined}
      fontSize={LEDGER_TOTAL_LABEL_FONT_SIZE}
      fontWeight={600}
      fill="var(--color-text-secondary)"
    >
      {formatYen(value)}
    </text>
  );
}

/**
 * チャートの吹き出し。区切り線の上に見えている売上高の系列を並べ、区切り線の下に売上高の合計（営業外収益は含めない）と、
 * 営業外収益が 0 でない期間はその値を分けて出す。区切り線の下は会計の値なので、凡例で系列（営業外収益を含む）を
 * 隠しても変えない。
 * @graph-connects formatYen [calls] 金額の書式
 */
function LedgerChartTooltip({
  active,
  payload,
  label,
  hiddenSeries,
}: Pick<TooltipContentProps, 'active' | 'payload' | 'label'> & {
  hiddenSeries: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const row = payload[0]?.payload as LedgerChartRow | undefined;
  if (!active || row === undefined) return null;
  return (
    <div style={{ ...TOOLTIP_CONTENT_STYLE, margin: 0, padding: 10, whiteSpace: 'nowrap' }}>
      <p style={{ margin: 0 }}>{label}</p>
      <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
        {SALES_SERIES.filter((series) => !hiddenSeries.has(series.key)).map((series) => (
          <li key={series.key} style={{ paddingTop: 4, color: SERIES_COLORS[series.key] }}>
            {t(series.labelKey)} : {formatYen(row[series.key])}
          </li>
        ))}
      </ul>
      <p
        style={{
          margin: '4px 0 0',
          paddingTop: 4,
          borderTop: '1px solid var(--color-border)',
          fontWeight: 600,
        }}
      >
        {t('revenue.ledger.salesTotal')} : {formatYen(row.salesTotal)}
      </p>
      {row.nonOperating !== 0 && (
        <p style={{ margin: 0, paddingTop: 4, color: SERIES_COLORS.nonOperating }}>
          {t('revenue.ledger.seriesNonOperating')} : {formatYen(row.nonOperating)}
        </p>
      )}
    </div>
  );
}

/**
 * 日次の取得の状況。invalidRange は日の範囲の指定が逆で取得しないとき。
 * @graph-connects none
 */
export type LedgerDailyStatus = 'loading' | 'error' | 'invalidRange' | 'ready';

/**
 * 日次・週次のチャートを描けない間に、空のチャートの代わりに出す文言。
 * @graph-connects none
 */
function DailyUnavailable({ status }: { status: Exclude<LedgerDailyStatus, 'ready'> }) {
  const { t } = useTranslation();
  if (status === 'loading') {
    return (
      <p className="py-16 text-center text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
    );
  }
  if (status === 'error') {
    return (
      <p role="alert" className="py-16 text-center text-sm text-[var(--color-error)]">
        {t('revenue.ledger.dailyError')}
      </p>
    );
  }
  return (
    <p role="alert" className="py-16 text-center text-sm text-[var(--color-text-secondary)]">
      {t('revenue.ledger.invalidRange')}
    </p>
  );
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計（正式版）のチャートタブ。売上高を勘定別に積み上げ（取消はゼロの下に積む）、営業外収益を別系列で出し、凡例で表示を切り替える。その期間の売上高の合計（税抜・営業外収益を含めない・凡例で隠しても変わらない）を、月次・四半期・年次は棒の上に（棒が多ければ縦書き、さらに多ければ出さない）、どの粒度でも吹き出しに出し、営業外収益は合計と分けて出す。日次・週次は前日からの増加で表し、どの日から出るか・どの日までが取引日で数え直した参考値かを API の応答の日付で添える。日次の読み込み中・失敗・期間の指定の誤りは空のチャートにせずそう伝える。
 * @graph-connects buildLedgerChartRows [calls] 粒度別のチャート行と期間の売上高の合計
 * @graph-connects ledgerTotalLabelLayout [calls] 棒の上の合計の数字の置き方
 * @graph-connects isLedgerIncreaseGranularity [calls] 日次・週次（前日からの増加で表す粒度）の判定
 * @graph-connects useSeriesToggle [calls] 凡例クリックでの系列の表示切替
 */
export function LedgerChartView({
  monthlyRows,
  dailyRows,
  dailyStatus,
  firstDate,
  referenceThrough,
  includeWithdrawal,
}: {
  monthlyRows: readonly RevenueLedgerRow[];
  dailyRows: readonly RevenueLedgerDailyRow[];
  /** 日次の取得の状況。読み込み中・失敗・期間の指定の誤りの間は日次・週次のチャートを空として描かない */
  dailyStatus: LedgerDailyStatus;
  /** 日次が出せる最初の日（API の応答のまま） */
  firstDate: string | null;
  /** 取引日で数え直した参考値で出している最後の日（API の応答のまま。参考値を使っていなければ null） */
  referenceThrough: string | null;
  includeWithdrawal: boolean;
}) {
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState<Granularity>('monthly');
  const [chartWidth, setChartWidth] = useState<number | null>(null);
  const { hiddenSeries, handleLegendClick } = useSeriesToggle();
  const rows = useMemo(
    () => buildLedgerChartRows({ granularity, monthlyRows, dailyRows, includeWithdrawal }),
    [granularity, monthlyRows, dailyRows, includeWithdrawal],
  );
  const labelLayout = useMemo(
    () =>
      ledgerTotalLabelLayout({
        granularity,
        plotWidth:
          chartWidth === null
            ? null
            : chartWidth - CHART_MARGIN.left - CHART_MARGIN.right - Y_AXIS_WIDTH,
        rows,
      }),
    [granularity, chartWidth, rows],
  );
  const { placement } = labelLayout;
  const isIncrease = isLedgerIncreaseGranularity(granularity);

  return (
    <div className="mt-6 space-y-4">
      <ChartFilterBar
        label={t('common.granularity')}
        options={GRANULARITY_OPTIONS}
        value={granularity}
        onChange={setGranularity}
      />
      {isIncrease && (
        <p className="text-xs text-[var(--color-text-secondary)]">
          {t('revenue.ledger.dailyNote')}
          {firstDate !== null && ` ${t('revenue.ledger.dailyFrom', { date: firstDate })}`}
          {referenceThrough !== null &&
            ` ${t('revenue.ledger.dailyReference', { date: referenceThrough })}`}
        </p>
      )}
      <Card className="p-4">
        <h3 className="mb-1 text-sm font-semibold text-[var(--color-text-primary)]">
          {t('revenue.ledger.chartTitle')}
        </h3>
        <p className="mb-1 text-xs text-[var(--color-text-muted)]">
          {t('revenue.ledger.legendHint')}
        </p>
        <p className="mb-3 text-xs text-[var(--color-text-muted)]">
          {t('revenue.ledger.salesTotalNote')}
        </p>
        {isIncrease && dailyStatus !== 'ready' ? (
          <DailyUnavailable status={dailyStatus} />
        ) : (
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer
              width="100%"
              height="100%"
              onResize={(width) => {
                setChartWidth(width);
              }}
            >
              <BarChart
                data={rows}
                stackOffset="sign"
                margin={{ ...CHART_MARGIN, top: labelLayout.marginTop }}
              >
                <CartesianGrid {...GRID_PROPS} />
                <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
                <YAxis
                  width={Y_AXIS_WIDTH}
                  tickCount={Y_AXIS_TICK_COUNT}
                  tick={AXIS_TICK_STYLE}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
                />
                <ReferenceLine y={0} stroke="var(--color-border)" />
                <Tooltip
                  includeHidden
                  content={(props: TooltipContentProps) => (
                    <LedgerChartTooltip
                      active={props.active}
                      payload={props.payload}
                      label={props.label}
                      hiddenSeries={hiddenSeries}
                    />
                  )}
                />
                <Legend {...LEGEND_CLICKABLE_PROPS} onClick={handleLegendClick} />
                {LEDGER_CHART_SERIES.map((series) => (
                  <Bar
                    key={series.key}
                    dataKey={series.key}
                    name={t(series.labelKey)}
                    stackId={series.stack}
                    fill={SERIES_COLORS[series.key]}
                    hide={hiddenSeries.has(series.key)}
                  />
                ))}
                {placement !== 'none' && (
                  <Bar
                    dataKey={zeroValue}
                    stackId="sales"
                    shape={EmptyShape}
                    legendType="none"
                    tooltipType="none"
                  >
                    <LabelList
                      dataKey="salesTotal"
                      content={<SalesTotalLabel placement={placement} />}
                    />
                  </Bar>
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </div>
  );
}
